import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search, Filter, Plus, ChevronLeft, ChevronRight,
  Calendar, MapPin, MessageSquare, RefreshCw, Tag, Layers, Trash2,
} from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import ToastMsg from '../../components/common/ToastMsg';
import {
  getEngageStatusBadge, getEngageSourceBadge,
  ENGAGE_STATUS_LABEL, ENGAGE_STATUS_ORDER, ENGAGE_SOURCE_LABEL,
} from '../../utils/engageStageBadge';
import { cn } from '../../utils/tailwindUtils';


const DATE_PRESETS = [
  { value: 'all',        label: 'All time' },
  { value: 'today',      label: 'Today' },
  { value: 'yesterday',  label: 'Yesterday' },
  { value: 'last7days',  label: 'Last 7 days' },
  { value: 'last30days', label: 'Last 30 days' },
];

const PAGE_SIZE = 25;


function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
}


export default function EnquiriesList() {
  const { fetch: apiFetch, remove: apiRemove } = useApiMethods({ throwOnFail: true });
  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (row, e) => {
    e.stopPropagation();
    const label = row.name || `Enquiry #${row.id}`;
    if (!window.confirm(`Delete enquiry for ${label}? This cannot be undone from the UI.`)) return;
    setDeletingId(row.id);
    try {
      await apiRemove(`engage/enquiries/${row.id}/`);
      setEnquiries(prev => prev.filter(x => x.id !== row.id));
      ToastMsg('success', `Deleted enquiry for ${label}`);
    } catch (err) {
      ToastMsg('error', err?.response?.data?.message || 'Failed to delete enquiry');
    } finally {
      setDeletingId(null);
    }
  };
  const navigate = useNavigate();
  const [urlParams] = useSearchParams();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  // Seed filter state from URL query params (?status=…&source=…&assigned_to=…&date=…&q=…)
  const [search, setSearch] = useState(urlParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState(urlParams.get('status') || 'all');
  const [sourceFilter, setSourceFilter] = useState(urlParams.get('source') || 'all');
  const [assignedFilter, setAssignedFilter] = useState(urlParams.get('assigned_to') || 'all');
  const [datePreset, setDatePreset] = useState(urlParams.get('date') || 'all');
  const [page, setPage] = useState(1);

  // Patient type filter, and a stage filter dependent on it — the stage options
  // are always the selected type's own ladder, never a fixed list.
  const [patientTypes, setPatientTypes] = useState([]);
  const [patientTypeFilter, setPatientTypeFilter] = useState(urlParams.get('patient_type') || 'all');
  const [stageFilter, setStageFilter] = useState(urlParams.get('stage') || 'all');
  const [typeStages, setTypeStages] = useState([]);

  useEffect(() => {
    let cancelled = false;
    apiFetch('engage/patient-types/').then(res => {
      if (cancelled) return;
      const rows = res?.results || res?.data?.results || [];
      setPatientTypes(Array.isArray(rows) ? rows : []);
    }).catch(() => { if (!cancelled) setPatientTypes([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Selecting "all types" collapses the stage filter, because a stage only has
  // meaning inside one ladder — two types can both own a stage called "Contacted".
  // The first run is skipped so a ?stage= URL param survives mount.
  const firstTypeRun = useRef(true);
  useEffect(() => {
    if (firstTypeRun.current) {
      firstTypeRun.current = false;
    } else {
      setStageFilter('all');
    }
    if (patientTypeFilter === 'all') { setTypeStages([]); return; }
    const found = patientTypes.find(t => String(t.id) === String(patientTypeFilter));
    setTypeStages(found?.stages || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientTypeFilter, patientTypes]);

  // Patient type is the primary lens, so picking one clears the legacy status
  // chips — the two are alternative views of the same pipeline and combining
  // them just yields confusing empty results.
  const pickType = (value) => {
    setPatientTypeFilter(value);
    setStatusFilter('all');
    setPage(1);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const resp = await apiFetch('engage/enquiries/');
        if (cancelled) return;
        const rows = resp?.results || resp?.data?.results || [];
        setEnquiries(Array.isArray(rows) ? rows : []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || 'Failed to load enquiries');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTick]);

  const filtered = useMemo(() => {
    const now = new Date();
    return enquiries.filter(row => {
      // Search
      if (search) {
        const q = search.trim().toLowerCase();
        const hay = [row.name, row.mobile, row.email, row.department_of_interest]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      // Status
      if (statusFilter !== 'all' && row.status !== statusFilter) return false;
      // Source
      if (sourceFilter !== 'all' && row.source !== sourceFilter) return false;
      // Patient type (primary), then a stage on that type
      if (patientTypeFilter === 'none') {
        if (row.patient_type) return false;
      } else if (patientTypeFilter !== 'all'
                 && String(row.patient_type || '') !== String(patientTypeFilter)) {
        return false;
      }
      if (stageFilter !== 'all' && String(row.stage || '') !== String(stageFilter)) return false;
      // Assigned to
      if (assignedFilter !== 'all' && String(row.assigned_to || '') !== String(assignedFilter)) return false;
      // Date preset
      if (datePreset !== 'all' && row.created_date) {
        const d = new Date(row.created_date);
        const daysAgo = (now - d) / 86400000;
        if (datePreset === 'today' && daysAgo > 1) return false;
        if (datePreset === 'yesterday' && (daysAgo > 2 || daysAgo <= 1)) return false;
        if (datePreset === 'last7days' && daysAgo > 7) return false;
        if (datePreset === 'last30days' && daysAgo > 30) return false;
      }
      return true;
    });
  }, [enquiries, search, statusFilter, sourceFilter, assignedFilter, datePreset,
      patientTypeFilter, stageFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  // Any filter that narrows the list can leave `page` past the last page, and
  // the pager hides itself below one page — so the table would render empty
  // with no way back. Clamp instead of trusting every handler to reset.
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const pageRows = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page],
  );

  // Legacy status counts — shown only while no patient type is selected.
  const stageCounts = useMemo(() => {
    const map = { all: enquiries.length };
    for (const s of ENGAGE_STATUS_ORDER) map[s] = 0;
    for (const e of enquiries) map[e.status] = (map[e.status] || 0) + 1;
    return map;
  }, [enquiries]);

  // Per-patient-type counts for the primary filter row.
  const typeCounts = useMemo(() => {
    const map = { all: enquiries.length, none: 0 };
    for (const e of enquiries) {
      if (e.patient_type) map[String(e.patient_type)] = (map[String(e.patient_type)] || 0) + 1;
      else map.none += 1;
    }
    return map;
  }, [enquiries]);

  // Counts per stage, scoped to the selected type so the numbers add up to that
  // type's total rather than the whole list.
  const stageChipCounts = useMemo(() => {
    const map = {};
    if (patientTypeFilter === 'all') return map;
    for (const e of enquiries) {
      if (String(e.patient_type || '') !== String(patientTypeFilter)) continue;
      if (e.stage) map[String(e.stage)] = (map[String(e.stage)] || 0) + 1;
    }
    return map;
  }, [enquiries, patientTypeFilter]);

  const selectedType = patientTypes.find(t => String(t.id) === String(patientTypeFilter));

  // Source-filter list (only sources that actually appear)
  const activeSources = useMemo(() => {
    const set = new Set();
    for (const e of enquiries) if (e.source) set.add(e.source);
    return Array.from(set);
  }, [enquiries]);

  return (
    <div className="p-4 max-w-[1600px] mx-auto space-y-3">
      {/* Title, search, selects and actions share ONE band. The decorative
          gradient tile is gone — the sidebar already tells you which page this
          is, and it was setting the row height on its own. */}
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-base font-bold text-gray-900 dark:text-white whitespace-nowrap">
          Patient Enquiries
        </h1>
        <span className="text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
          {loading ? 'Loading…' : `${filtered.length} of ${enquiries.length}`}
        </span>

        <div className="relative flex-1 min-w-[200px] max-w-sm ml-1">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search name, mobile, email…"
            className="w-full pl-8 pr-2.5 py-1.5 text-[13px] rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white placeholder-gray-400"
          />
        </div>
        <select
          value={sourceFilter}
          onChange={e => { setSourceFilter(e.target.value); setPage(1); }}
          className="px-2 py-1.5 text-[12px] rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">All sources</option>
          {activeSources.map(s => (
            <option key={s} value={s}>{ENGAGE_SOURCE_LABEL[s] || s}</option>
          ))}
        </select>
        <select
          value={datePreset}
          onChange={e => { setDatePreset(e.target.value); setPage(1); }}
          className="px-2 py-1.5 text-[12px] rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
        >
          {DATE_PRESETS.map(p => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>

        <div className="flex items-center gap-1.5 ml-auto">
          <button
            onClick={() => setReloadTick(t => t + 1)}
            title="Reload enquiries"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-[13px] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button
            onClick={() => navigate('/engage/enquiries/new')}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-primary text-white text-[13px] shadow-sm hover:bg-primary/90 whitespace-nowrap"
          >
            <Plus className="w-3.5 h-3.5" /> New Enquiry
          </button>
        </div>
      </div>

      {/* ── PRIMARY FILTER: patient type. Label sits inline with the pills so the
             two filter tiers cost two lines total, not four. ── */}
      <div className="flex items-start gap-2">
        <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold pt-1.5 w-[52px] flex-shrink-0">
          <Tag className="w-3 h-3 flex-shrink-0" /> Type
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => pickType('all')}
            className={cn(
              'px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-colors',
              patientTypeFilter === 'all'
                ? 'bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50',
            )}
          >
            All types <span className="ml-1 opacity-70">{typeCounts.all}</span>
          </button>
          {patientTypes.map(t => (
            <button
              key={t.id}
              onClick={() => pickType(String(t.id))}
              className={cn(
                'px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-colors',
                String(patientTypeFilter) === String(t.id)
                  ? (t.colour || 'bg-violet-100 text-violet-700') + ' border-transparent ring-2 ring-current'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50',
              )}
            >
              {t.name}
              <span className="ml-1 opacity-70">{typeCounts[String(t.id)] || 0}</span>
            </button>
          ))}
          {typeCounts.none > 0 && (
            <button
              onClick={() => pickType('none')}
              className={cn(
                'px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-colors',
                patientTypeFilter === 'none'
                  ? 'bg-gray-200 text-gray-800 border-transparent ring-2 ring-current dark:bg-gray-600 dark:text-gray-100'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50',
              )}
            >
              Unassigned <span className="ml-1 opacity-70">{typeCounts.none}</span>
            </button>
          )}
        </div>
      </div>

      {/* ── SECONDARY: this type's own stage ladder, or the legacy status list ── */}
      <div className="flex items-start gap-2">
        <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold pt-1.5 w-[52px] flex-shrink-0"
             title={selectedType ? `${selectedType.name} — ${typeStages.length} stages` : 'Legacy status buckets'}>
          <Layers className="w-3 h-3 flex-shrink-0" /> {selectedType ? 'Stage' : 'Status'}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {selectedType ? (
            <>
              <button
                onClick={() => { setStageFilter('all'); setPage(1); }}
                className={cn(
                  'px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors',
                  stageFilter === 'all'
                    ? 'bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900'
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50',
                )}
              >
                All stages <span className="ml-1 opacity-70">{typeCounts[String(selectedType.id)] || 0}</span>
              </button>
              {typeStages.map(s => (
                <button
                  key={s.id}
                  onClick={() => { setStageFilter(String(s.id)); setPage(1); }}
                  className={cn(
                    'px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors',
                    String(stageFilter) === String(s.id)
                      ? (s.colour || 'bg-blue-100 text-blue-700') + ' border-transparent ring-1 ring-current'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50',
                  )}
                >
                  {s.name}
                  <span className="ml-1 opacity-70">{stageChipCounts[String(s.id)] || 0}</span>
                </button>
              ))}
            </>
          ) : (
            <>
              <button
                onClick={() => { setStatusFilter('all'); setPage(1); }}
                className={cn(
                  'px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors',
                  statusFilter === 'all'
                    ? 'bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900'
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50',
                )}
              >
                All <span className="ml-1 opacity-70">{stageCounts.all}</span>
              </button>
              {ENGAGE_STATUS_ORDER.map(s => (
                <button
                  key={s}
                  onClick={() => { setStatusFilter(s); setPage(1); }}
                  className={cn(
                    'px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors',
                    statusFilter === s
                      ? getEngageStatusBadge(s) + ' border-transparent ring-1 ring-current'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50',
                  )}
                >
                  {ENGAGE_STATUS_LABEL[s]}
                  <span className="ml-1 opacity-70">{stageCounts[s] || 0}</span>
                </button>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Search + filters bar */}
      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-gray-100 dark:border-gray-700">
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="px-3 py-2.5 font-medium whitespace-nowrap">Patient</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap">Source</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap">Department</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap">Procedure &amp; quote</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap">Stage</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap">Assigned to</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap">Follow-up</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap">Created</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap w-[1%]"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">Loading…</td></tr>
              ) : pageRows.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                  {enquiries.length ? 'No enquiries match your filters.' : 'No enquiries yet — click "New Enquiry" to add the first one.'}
                </td></tr>
              ) : pageRows.map(row => (
                <tr
                  key={row.id}
                  onClick={() => navigate(`/engage/enquiries/${row.id}`)}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
                >
                  {/* Every cell is kept to a single line so row height stays ~30px.
                      Secondary text that used to sit on its own line (message,
                      patient type, roll-up status) moved into title tooltips — the
                      information is still reachable, it just no longer costs a row. */}
                  <td className="px-3 py-2.5 max-w-[190px]">
                    <div className="flex items-baseline gap-2 min-w-0">
                      <span className="font-medium text-gray-900 dark:text-white truncate">{row.name}</span>
                      {row.mobile && (
                        <span className="text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap tabular-nums">
                          {row.mobile}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={cn('inline-block px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap', getEngageSourceBadge(row.source))}>
                      {ENGAGE_SOURCE_LABEL[row.source] || row.source}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 max-w-[150px]"
                      title={row.doctor_of_interest ? `Dr. ${row.doctor_of_interest}` : undefined}>
                    <div className="truncate text-gray-700 dark:text-gray-300 text-[12px]">
                      {row.department_of_interest || <span className="text-gray-400">—</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 max-w-[240px]" title={row.message || undefined}>
                    <div className="flex items-baseline gap-2 min-w-0">
                      <span className="truncate text-gray-800 dark:text-gray-200 text-[12px]">
                        {row.procedure_of_interest || <span className="text-gray-400 italic">not set</span>}
                      </span>
                      {row.expected_treatment_value != null && Number(row.expected_treatment_value) > 0 && (
                        <span className="text-emerald-700 dark:text-emerald-300 text-[11px] font-semibold tabular-nums whitespace-nowrap">
                          ₹{Number(row.expected_treatment_value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5"
                      title={row.stage_name
                        ? `${row.patient_type_name} → ${row.stage_name} (reports as ${ENGAGE_STATUS_LABEL[row.status] || row.status})`
                        : undefined}>
                    <span className={cn('inline-block px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap',
                      row.stage_name ? (row.stage_colour || getEngageStatusBadge(row.status)) : getEngageStatusBadge(row.status))}>
                      {row.stage_name || ENGAGE_STATUS_LABEL[row.status] || row.status}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-gray-700 dark:text-gray-300 text-[12px] max-w-[120px]">
                    <div className="truncate">
                      {row.assigned_to_name || row.assigned_to || <span className="text-gray-400">—</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {row.followup_datetime && !['converted', 'dropped', 'lost'].includes(row.status) ? (
                      <span className="inline-flex items-center gap-1 text-[11px]">
                        <Calendar className="w-3 h-3 text-blue-500 flex-shrink-0" />
                        {fmtDate(row.followup_datetime)}
                      </span>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {fmtDate(row.created_date)}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button
                      onClick={(e) => handleDelete(row, e)}
                      disabled={deletingId === row.id}
                      title="Delete enquiry"
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-3 py-2.5 border-t border-gray-100 dark:border-gray-700 text-[12px]">
            <div className="text-gray-600 dark:text-gray-400">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
              ><ChevronLeft className="w-4 h-4" /></button>
              <span className="px-3 text-gray-700 dark:text-gray-300">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
              ><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
